import React, { useCallback, useState } from 'react';

import { DetailsList, DetailsListItem } from '@eika/details-list';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../defaultConfig';
import { useDeleteAuthorizedCustomer } from '../../fetchHooks/useAuthorizedCustomers';
import type { Account, AuthorizedCustomer } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import { accountTrackingIDs } from '../../utils/gtm/trackIDs/account';
import DeleteAuthorizedCustomer from '../AddAuthorizedCustomer/DeleteAuthorizedCustomer';
import { EikaModal } from '../EikaModal';

import './AccountDetailsView.scss';

interface Props {
    account?: Account;
    authorizedCustomers?: AuthorizedCustomer[];
    isLoading: boolean;
}

const AccountAuthorizedCustomers: React.FC<Props> = ({ account, authorizedCustomers, isLoading }) => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    const [showModal, setShowModal] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState<AuthorizedCustomer>();

    const canDeleteDisponent = account?.rights.addAuthorizeCustomers && featureToggles.enableAddAuthorizedCustomer;
    const deleteAuthorizedCustomer = useDeleteAuthorizedCustomer(account?.accountId);

    const openModal = (authorizedCustomer: AuthorizedCustomer) => {
        setShowModal(true);
        setCustomerToDelete(authorizedCustomer);
        deleteAuthorizedCustomer.reset();
    };
    const closeModal = () => {
        setShowModal(false);
        setCustomerToDelete(undefined);
    };

    const deleteDisponent = useCallback(() => {
        if (customerToDelete?.customerNumber) {
            deleteAuthorizedCustomer.mutate(customerToDelete.customerNumber);
        }
    }, [customerToDelete, deleteAuthorizedCustomer]);

    return (
        <>
            <DetailsList title={translated.accountDetails.accountAccess} isLoading={isLoading} skeletonCount={2}>
                {authorizedCustomers && authorizedCustomers.length > 0 ? (
                    <>
                        {authorizedCustomers.map((customer) => (
                            <DetailsListItem
                                label={translated.customerRole[customer.role]}
                                value={customer.name}
                                key={`account-details__owner-${customer.name}`}
                                button={
                                    canDeleteDisponent && customer.role === 'DISPOSAL'
                                        ? {
                                              title: translated.accountDetails.addAuthorizedCustomer
                                                  .deleteAuthorizedCustomer,
                                              onClick: () => openModal(customer),
                                          }
                                        : undefined
                                }
                                icon={IconTypes.SLETT_24}
                            />
                        ))}
                    </>
                ) : (
                    <>
                        {account?.owners.map((owner) => (
                            <DetailsListItem
                                label={
                                    owner.primaryOwner && account.owners.length > 1
                                        ? translated.accountDetails.mainOwner
                                        : translated.accountDetails.owner
                                }
                                value={owner.name}
                                key={`account-details__owner-${owner.id}`}
                            />
                        ))}
                    </>
                )}
            </DetailsList>
            {featureToggles.enableAddAuthorizedCustomer && authorizedCustomers && authorizedCustomers.length > 0 && (
                <EikaModal
                    id="removeAuthorizedCustomer"
                    onClose={closeModal}
                    setOpen={setShowModal}
                    open={showModal}
                    contentShouldStretch
                    appNamespace="dagligbank-lokalbank-web"
                    dataTrackName={accountTrackingIDs.deleteAuthorizedCustomer.name}
                >
                    {customerToDelete && (
                        <DeleteAuthorizedCustomer
                            success={deleteAuthorizedCustomer.isSuccess}
                            error={deleteAuthorizedCustomer.error ?? undefined}
                            onClose={closeModal}
                            deleteDisponent={deleteDisponent}
                            name={customerToDelete.name}
                            isLoading={deleteAuthorizedCustomer.isPending}
                        />
                    )}
                </EikaModal>
            )}
        </>
    );
};

export default AccountAuthorizedCustomers;
